// Reference recipes: https://www.3dink.ee/hestia/data/data.js and the supplied
// CO491, Cottage Diagonal C30 and Smartline drawings. Studio contours remain
// authoritative for finished sizes. These recipes are review data, not a CNC release.
import { cottageGlazedCore } from './cottageCore.js';
export const MANUFACTURING_RECIPE = 'hestia-reference-2026-11-02-hinge-bands';
const mm = n => Number(n.toFixed(4));

export function readDoorHardware(hardware = {}) {
  const result = { lockType: hardware.lockType ?? 'ASSA', hinges: hardware.hinges ?? 2 };
  if (!['ASSA', '3PL'].includes(result.lockType)) throw new Error('Choose ASSA or 3PL lock.');
  if (![2, 3].includes(result.hinges)) throw new Error('Choose two or three hinges.');
  return result;
}

/** All heights are from the leaf bottom, or the SAME finished-floor datum.
 * A top waternose changes the body, never the hardware's nominal datum. */
export function resolveDoorHardware({ family, threshold, hardware, nominalHeightMm, bodyHeightMm, assemblyHeightMm, leafBottomMm }) {
  const selected = readDoorHardware(hardware);
  const centredLock = threshold === 'threshold_pine' && ['MERANTI', 'COTTAGE'].includes(family);
  // User-specified DoorHeight bands apply to every family/threshold. DoorHeight
  // is the outer assembly height; these offsets are measured on the leaf.
  // Round millimetres before choosing the band to avoid 3D metre conversion noise.
  const doorHeightMm = mm(assemblyHeightMm);
  const bottomInsetMm = doorHeightMm <= 1977 ? 197 : doorHeightMm <= 2077 ? 222 : 247;
  const waternoseReductionMm = mm(nominalHeightMm - bodyHeightMm);
  const topInsetMm = mm(bottomInsetMm - waternoseReductionMm);
  const centersMm = [bottomInsetMm, ...(selected.hinges === 3 ? [nominalHeightMm / 2] : []), bodyHeightMm - topInsetMm].map(mm);
  const centerMm = selected.lockType === '3PL' ? 1055 : centredLock ? nominalHeightMm / 2 : 1035 - leafBottomMm;
  const timber = ['MERANTI', 'COTTAGE', '18_DEG'].includes(family);
  const lock = { type: selected.lockType, centerMm: mm(centerMm), floorCenterMm: mm(centerMm + leafBottomMm),
    topMm: mm(bodyHeightMm - centerMm), rule: selected.lockType === '3PL' ? '1055 from nominal leaf bottom' : centredLock ? 'Nominal leaf height / 2' : '1035 from finished floor',
    // 3PL templates disagree on remote pockets and backsets. Keep their
    // explicit centre datum, without substituting an ASSA machining template.
    faceplateHeightMm: selected.lockType === 'ASSA' ? 225 : null,
    faceplateWidthMm: selected.lockType === 'ASSA' ? 22 : null,
    faceplateDepthMm: selected.lockType === 'ASSA' ? 3 : null,
    pocketHeightMm: selected.lockType === 'ASSA' ? 166 : null,
    pocketWidthMm: selected.lockType === 'ASSA' ? 19.5 : null,
    caseDepthMm: selected.lockType === 'ASSA' ? (timber ? 72.6 : 73) : null,
    backsetMm: selected.lockType === 'ASSA' ? (family === 'COTTAGE' ? 50 : timber ? 50.43 : 50.6) : null,
    boreDiameterMm: selected.lockType === 'ASSA' ? 43 : null,
    boreOffsetMm: selected.lockType === 'ASSA' ? 55.5 : null,
    edgeOffsetMm: selected.lockType === 'ASSA' ? (timber ? 12.7 : 12.8) : null };
  const hinges = { count: selected.hinges, centersMm, floorCentersMm: centersMm.map(y => mm(y + leafBottomMm)),
    topMarginsMm: centersMm.map(y => mm(bodyHeightMm-y)), spanMm: mm(centersMm.at(-1)-centersMm[0]),
    bottomInsetMm, topInsetMm, waternoseReductionMm,
    rule: `DoorHeight ${doorHeightMm}: bottom ${bottomInsetMm}; top ${bottomInsetMm} - ${waternoseReductionMm} Waternose = ${topInsetMm}`,
    heightMm: 110, widthMm: 36, radiusMm: 12.5, leafDepthMm: 3, frameDepthMm: 3.6 };
  const issues = [];
  if (centersMm.some(y => y-55 < 0 || y+55 > bodyHeightMm) || centersMm.some((y,i) => i && y-centersMm[i-1] < 110)) {
    issues.push('Hinge pockets do not fit this leaf height; revise the hardware layout.');
  }
  if (centerMm < 112.5 || centerMm + 112.5 > bodyHeightMm) issues.push('Lock does not fit this leaf height.');
  if (selected.lockType === '3PL') issues.push('3PL centre datum is mapped; confirm the lock model and remote-pocket template before machining.');
  return { selected, lock, hinges, issues };
}

export function resolveManufacturing(product, geometry, hardware) {
  const { leaf: l, assembly: a, clearances: gap } = geometry;
  const hw = resolveDoorHardware({ family: product.family, threshold: product.threshold, hardware,
    nominalHeightMm: l.nominalHeightMm, bodyHeightMm: l.heightMm, assemblyHeightMm: a.heightMm, leafBottomMm: gap.bottomMm });
  const railMm = product.family === 'COTTAGE' ? 39.7 : ['MERANTI', '18_DEG'].includes(product.family) ? 33.1 : 45;
  const core = { widthMm: mm(l.widthMm+5), heightMm: mm(l.heightMm+5), allowanceMm: 5, railMm,
    stileMm: product.family === '18_DEG' ? railMm*2 : railMm,
    recipe: geometry.openings.length ? 'cottage-glazed' :
      product.family === 'COTTAGE' && product.cottagePattern === 'chevron' ? 'cottage-diagonal' :
      ['MERANTI', 'COTTAGE'].includes(product.family) ? 'middle-rails' : 'perimeter', parts: [], issues: [] };
  const part = (name, material, x, y, w, h) => {
    if (w <= 0 || h <= 0) { core.issues.push(`${name} does not fit the core.`); return; }
    core.parts.push({ name, material, xMm: mm(x), yMm: mm(y), widthMm: mm(w), heightMm: mm(h) });
  };
  const w=core.widthMm, h=core.heightMm, r=railMm, s=core.stileMm, iw=w-2*s, ih=h-2*r;
  // Continuous stiles; rails fitted between them, matching the reference core.
  if(product.family==='18_DEG') {
    part('Left outer stile','timber',0,0,r,h); part('Left inner stile','timber',r,0,r,h);
    part('Right inner stile','timber',w-2*r,0,r,h); part('Right outer stile','timber',w-r,0,r,h);
  } else { part('Left stile', 'timber', 0,0,s,h); part('Right stile','timber',w-s,0,s,h); }
  part('Bottom rail','timber',s,0,iw,r); part('Top rail','timber',s,h-r,iw,r);
  if (core.recipe === 'cottage-glazed') {
    Object.assign(core,cottageGlazedCore(core,geometry,product,hw.lock));
  } else if (core.recipe === 'middle-rails') {
    const epsH=(h-607-4*r)/2, middleY=r+epsH+r;
    part('Lower middle rail','timber',s,r+epsH,iw,r);
    part('Upper middle rail','timber',s,middleY+607,iw,r);
    const lockOnLeft=product.handing==='right';
    part('Lock stile','timber',lockOnLeft?s:w-s-r,middleY,r,607);
    part('Lower EPS','EPS',s,r,iw,epsH); part('Upper EPS','EPS',s,h-r-epsH,iw,epsH);
    part('Middle EPS','EPS',s+(lockOnLeft?r:0),middleY,iw-r,607);
  } else if (core.recipe === 'cottage-diagonal') {
    // Supplied diagonal Cottage sheet: two full-height centre stiles.
    part('Centre stile L','timber',w/2-r,r,r,ih); part('Centre stile R','timber',w/2,r,r,ih);
    part('Left EPS','EPS',s,r,iw/2-r,ih); part('Right EPS','EPS',w/2+r,r,iw/2-r,ih);
    core.issues.push('Diagonal Cottage lock reinforcement and panel fixings need confirmation; centre stiles follow the supplied C30 core sheet.');
  } else part('EPS','EPS',s,r,iw,ih);
  if (geometry.openings.length && core.recipe !== 'cottage-glazed') core.issues.push('Window openings are overlaid at the finished-leaf datum; core reinforcement around these openings is not specified.');
  const striker = hw.lock.type === 'ASSA' ? { centerFloorMm: hw.lock.floorCenterMm,
    faceplateHeightMm: product.family === 'MERANTI' ? 135 : 200,
    // The supplied Meranti/Cottage details are verified; Smartline uses a
    // different rebate and requires its own pocket-position approval.
    pocketHeightMm: product.family === 'MERANTI' ? 67 : ['COTTAGE','60_OUT','60_IN','60_PLUS'].includes(product.family) ? 146 : null,
    pocketWidthMm: product.family === 'MERANTI' ? 18 : ['COTTAGE','60_OUT','60_IN','60_PLUS'].includes(product.family) ? 30 : null,
    pocketRadiusMm: ['MERANTI','COTTAGE','60_OUT','60_IN','60_PLUS'].includes(product.family) ? 8 : null,
    ...(['COTTAGE','60_OUT','60_IN','60_PLUS'].includes(product.family)?{faceplateWidthMm:40,faceplateRadiusMm:15,faceplateDepthMm:2,
      pocketDepthMm:19,pocketEdgeOffsetMm:7}: {}) } : null;
  const issues = [...hw.issues, ...core.issues,
    'Reference core and hardware recipes require production approval for the selected profiles. Frame machining is located from finished floor; jamb end and rebate-depth operations need confirmation.'];
  return { recipe: MANUFACTURING_RECIPE, ...hw, core, striker, issues };
}
